import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, del, get, post, put } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';

interface KeyInfo {
  id: string;
  group: 'ai' | 'feeds';
  env: string;
  stored: boolean;
  hint: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  envSet: boolean;
  source: 'settings' | 'env' | 'none';
  error: string | null;
}
interface KeysDto {
  canStore: boolean;
  items: KeyInfo[];
}
interface TestResult {
  ok: boolean;
  ms?: number;
  feeds?: number;
  names?: string[];
  model?: string;
  voices?: number;
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** What each key is, where it comes from, and which page uses it. */
const META: Record<string, { name: string; desc: () => string; placeholder: string; page: { to: string; label: () => string } }> = {
  'anthropic.apiKey': { name: 'Anthropic (Claude)', desc: () => t('keyAnthropicDesc'), placeholder: 'sk-ant-…', page: { to: '/admin/ai', label: () => t('aiMenu') } },
  'ap.mediaApiKey': { name: 'Associated Press · AP Media API', desc: () => t('keyApDesc'), placeholder: 'AP Media API key', page: { to: '/admin/feeds', label: () => t('feedsMenu') } },
  'elevenlabs.apiKey': { name: 'ElevenLabs · φωνή AI', desc: () => t('keyElevenDesc'), placeholder: 'xi-…', page: { to: '/video-hub', label: () => t('videoHubMenu') } },
};

function KeyCard({ item, canStore, onData }: { item: KeyInfo; canStore: boolean; onData: (d: KeysDto, notice: string) => void }) {
  const meta = META[item.id] ?? { name: item.id, desc: () => '', placeholder: '', page: null };
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState<'save' | 'remove' | 'test' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<TestResult | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy('save');
    setError(null);
    setTest(null);
    try {
      const d = await put<KeysDto>(`/api/admin/credentials/${item.id}`, { value: value.trim() });
      setValue('');
      onData(d, `${meta.name}: ${t('keySaved')}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!window.confirm(t('keyRemoveConfirm'))) return;
    setBusy('remove');
    setError(null);
    setTest(null);
    try {
      onData(await del<KeysDto>(`/api/admin/credentials/${item.id}`), `${meta.name}: ${t('keyRemoved')}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const runTest = async () => {
    setBusy('test');
    setError(null);
    setTest(null);
    try {
      setTest(await post<TestResult>(`/api/admin/credentials/${item.id}/test`));
    } catch (err) {
      setTest({ ok: false });
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };

  const chip = item.error ? (
    <span className="status err">{item.error}</span>
  ) : item.source === 'settings' ? (
    <span className="status ok">
      {t('keyStored')} …{item.hint}
    </span>
  ) : item.source === 'env' ? (
    <span className="status info">{t('keyFromEnv')}</span>
  ) : (
    <span className="status warn">{t('keyNotSet')}</span>
  );
  const inUse = item.source !== 'none';

  return (
    <form onSubmit={save} className="card key-card">
      <div className="key-head">
        <b>{meta.name}</b> {chip}
      </div>
      <p className="muted small">
        {meta.desc()}{' '}
        {meta.page && (
          <Link to={meta.page.to}>
            {meta.page.label()} ›
          </Link>
        )}
      </p>
      <div className="small muted">
        {item.stored && item.updatedAt && (
          <>
            {t('updated')}: {fmtDateTime(item.updatedAt)}
            {item.updatedBy ? ` ${t('keyBy')} ${item.updatedBy}` : ''}
            {item.envSet ? ` · ${t('keyEnvToo')}` : ''}
          </>
        )}
        {!item.stored && item.envSet && `${t('keyEnvOnly')} (${item.env}).`}
      </div>
      <div className="key-row">
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={!canStore || busy !== null}
          placeholder={item.stored ? t('keyReplace') : meta.placeholder}
          aria-label={`${meta.name} ${t('keyNew')}`}
        />
        <button className="btn primary" disabled={!canStore || busy !== null || !value.trim()}>
          💾 {t('save')}
        </button>
        {item.stored && (
          <button type="button" className="btn" disabled={busy !== null} onClick={() => void remove()}>
            ✖ {t('keyRemove')}
          </button>
        )}
        <button type="button" className="btn" disabled={busy !== null || !inUse} onClick={() => void runTest()}>
          ⚡ {t('keyTest')}
        </button>
        {busy === 'test' && <span className="muted">{t('keyTesting')}</span>}
      </div>
      {error && <div className="banner err">{error}</div>}
      {test && (
        <div className={`banner ${test.ok ? 'info' : 'err'}`}>
          {!test.ok
            ? t('keyTestFailed')
            : item.id === 'elevenlabs.apiKey'
              ? `${t('keyTestElevenOk')} · ${test.voices ?? 0} ${t('keyTestElevenVoices')}${test.names?.length ? `: ${test.names.join(', ')}` : ''} · ${test.ms} ms`
              : item.id === 'ap.mediaApiKey'
              ? `${t('keyTestApOk')} · ${test.feeds ?? 0} ${t('keyTestApFeeds')}${test.names?.length ? `: ${test.names.join(', ')}` : ''} · ${test.ms} ms`
              : `${t('aiTestOk')} · ${test.model} · ${test.ms} ms`}
        </div>
      )}
    </form>
  );
}

/** Διαχείριση → Κλειδιά API: every outside service's key in one place, sealed on the server, never shown again. */
export function AdminKeysPage() {
  const { can } = useSession();
  const [data, setData] = useState<KeysDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<KeysDto>('/api/admin/credentials')
      .then(setData)
      .catch((e) => setError(errText(e)));
  }, []);

  if (!can('admin.config')) return <div className="banner err">{t('noPermission')}</div>;

  return (
    <>
      <PageTitle title={t('keysTitle')} hint={t('keysHint')} />
      {error && <div className="banner err">{error}</div>}
      {notice && <div className="banner info">{notice}</div>}
      {data && !data.canStore && <div className="banner warn">{t('aiNoSecret')}</div>}
      <div className="key-list">
        {data?.items.map((item) => (
          <KeyCard
            key={item.id}
            item={item}
            canStore={data.canStore}
            onData={(d, n) => {
              setData(d);
              setNotice(n);
            }}
          />
        ))}
      </div>
      <p className="tip">{t('keysTip')}</p>
    </>
  );
}
